import React, { useState, useEffect, useCallback } from "react";
import { applyLeave, getLeaveBalance } from "../../../services/attendanceApi";
import Icons from "./Icons";

/**
 * LeaveApplyForm — apply for leave.
 * Types: CL / SL / EL / UNPAID / COMP_OFF. Supports half-day.
 *
 * Balance-aware: fetches the employee's leave balances and DISABLES any
 * leave type whose remaining balance is 0 (except UNPAID, which has no
 * quota). If the selected type is exhausted, the submit button is blocked
 * with a clear "no balance left" message.
 */
const LEAVE_TYPES = [
  { value: "CL", label: "Casual Leave (CL)" },
  { value: "SL", label: "Sick Leave (SL)" },
  { value: "EL", label: "Earned Leave (EL)" },
  { value: "UNPAID", label: "Unpaid Leave (LOP)" },
  { value: "COMP_OFF", label: "Compensatory Off" },
];

// UNPAID has no balance limit — always allowed.
const NO_QUOTA = ["UNPAID"];

function LeaveApplyForm({ onApplied, refreshKey }) {
  const [form, setForm] = useState({
    leaveType: "CL",
    fromDate: "",
    toDate: "",
    isHalfDay: false,
    halfSession: "MORNING",
    reason: "",
  });
  const [balances, setBalances] = useState({}); // { CL: {remaining, allocated}, ... }
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Load balances so we can disable exhausted types
  const loadBalances = useCallback(async () => {
    try {
      const res = await getLeaveBalance();
      const data = res && res.data ? res.data : res;
      const list = Array.isArray(data) ? data : (data.balances || []);
      const map = {};
      list.forEach((b) => {
        const type = b.leaveType || b.leave_type;
        if (type) {
          map[type] = {
            remaining: Number(b.remaining ?? (Number(b.allocated || 0) - Number(b.used || 0))),
            allocated: Number(b.allocated || 0),
          };
        }
      });
      setBalances(map);
    } catch {
      setBalances({}); // if it fails, don't block — backend still validates
    }
  }, []);

  useEffect(() => { loadBalances(); }, [loadBalances, refreshKey]);

  // Is a given leave type exhausted?
  const isExhausted = (type) => {
    if (NO_QUOTA.includes(type)) return false;
    const b = balances[type];
    if (!b) return false; // unknown balance — let backend decide
    return b.remaining <= 0;
  };

  const remainingText = (type) => {
    if (NO_QUOTA.includes(type)) return "no limit";
    const b = balances[type];
    if (!b) return "";
    return `${b.remaining} left`;
  };

  const selectedExhausted = isExhausted(form.leaveType);

  const validate = () => {
    if (selectedExhausted) return "No balance left for this leave type. Choose another type or Unpaid leave.";
    if (!form.fromDate) return "Please select a from date.";
    if (!form.isHalfDay && !form.toDate) return "Please select a to date.";
    if (!form.isHalfDay && form.toDate < form.fromDate) return "To date cannot be before from date.";
    if (!form.reason.trim()) return "Please enter a reason.";
    return "";
  };

  const handleSubmit = async () => {
    setMsg("");
    setError("");
    const v = validate();
    if (v) { setError(v); return; }

    try {
      setSubmitting(true);
      const payload = {
        leaveType: form.leaveType,
        fromDate: form.fromDate,
        toDate: form.isHalfDay ? form.fromDate : form.toDate,
        isHalfDay: form.isHalfDay,
        halfSession: form.isHalfDay ? form.halfSession : null,
        reason: form.reason.trim(),
      };
      await applyLeave(payload);
      setMsg("Leave request submitted for approval.");
      setForm({ leaveType: "CL", fromDate: "", toDate: "", isHalfDay: false, halfSession: "MORNING", reason: "" });
      loadBalances();
      onApplied && onApplied();
    } catch (err) {
      setError(err.message || "Could not submit leave request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="atl-card atl-leaveform">
      <h3><span className="atl-h3ic">{Icons.leave}</span>Apply for Leave</h3>

      {msg && <div className="atl-success">{msg}</div>}
      {error && <div className="atl-error">{error}</div>}

      <div className="atl-field">
        <label>Leave Type</label>
        <select className="atl-input" value={form.leaveType} onChange={(e) => set("leaveType", e.target.value)}>
          {LEAVE_TYPES.map((t) => {
            const exhausted = isExhausted(t.value);
            const info = remainingText(t.value);
            return (
              <option key={t.value} value={t.value} disabled={exhausted}>
                {t.label}{info ? ` — ${info}` : ""}{exhausted ? " (no balance)" : ""}
              </option>
            );
          })}
        </select>
      </div>

      {selectedExhausted && (
        <div className="atl-warn-inline">
          No balance left for this leave type. Please choose another type or apply Unpaid leave.
        </div>
      )}

      <div className="atl-field atl-field--check">
        <label>
          <input type="checkbox" checked={form.isHalfDay} onChange={(e) => set("isHalfDay", e.target.checked)} />
          Half-day leave
        </label>
      </div>

      <div className="atl-row">
        <div className="atl-field">
          <label>{form.isHalfDay ? "Date" : "From Date"}</label>
          <input type="date" className="atl-input" value={form.fromDate} onChange={(e) => set("fromDate", e.target.value)} />
        </div>
        {!form.isHalfDay && (
          <div className="atl-field">
            <label>To Date</label>
            <input type="date" className="atl-input" min={form.fromDate || undefined} value={form.toDate} onChange={(e) => set("toDate", e.target.value)} />
          </div>
        )}
        {form.isHalfDay && (
          <div className="atl-field">
            <label>Session</label>
            <select className="atl-input" value={form.halfSession} onChange={(e) => set("halfSession", e.target.value)}>
              <option value="MORNING">Morning</option>
              <option value="AFTERNOON">Afternoon</option>
            </select>
          </div>
        )}
      </div>

      <div className="atl-field">
        <label>Reason</label>
        <textarea className="atl-input" rows={3} value={form.reason} onChange={(e) => set("reason", e.target.value)} placeholder="Reason for leave" />
      </div>

      <button className="atl-btn atl-btn--primary" onClick={handleSubmit} disabled={submitting || selectedExhausted}>
        {submitting ? "Submitting..." : "Submit for Approval"}
      </button>
    </div>
  );
}

export default LeaveApplyForm;
